import { Link, useParams } from 'react-router-dom';
import {
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  LightBulbIcon,
  BeakerIcon
} from '@heroicons/react/24/outline';
import Navbar from '../../components/Navbar';
import Chatbot from '../../components/Chatbot';

export default function BulkificationPage() {
  const { orgId } = useParams();
  const basePath = orgId ? `/${orgId}` : '';

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <Link to={`${basePath}/help`} className="text-blue-600 hover:text-blue-800 text-sm font-medium">
            ← Back to Help Center
          </Link>
          <h1 className="text-3xl font-bold text-gray-900 mt-4">Flow Bulkification</h1>
          <p className="text-lg text-gray-600 mt-2">
            Processing multiple records efficiently in record-triggered flows
          </p>
        </div>

        {/* Important Note */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-4 mb-8">
          <div className="flex">
            <ExclamationTriangleIcon className="h-6 w-6 text-yellow-400 mr-3 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Critical for Record-Triggered Flows</h3>
              <p className="mt-2 text-sm text-yellow-700">
                If your flow is triggered by record changes (Before Save, After Save, Before Delete, After Delete),
                bulkification is essential. Without it, your flow will fail when Salesforce processes multiple records at once.
              </p>
            </div>
          </div>
        </div>

        {/* What is Bulkification? */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">What is Bulkification?</h2>

          <p className="text-gray-700 mb-4">
            Bulkification is the practice of designing your flow to handle multiple trigger records in a single execution,
            rather than processing them one at a time.
          </p>

          <div className="bg-blue-50 p-4 rounded-lg mb-4">
            <h3 className="font-semibold text-blue-900 mb-2">Why Multiple Records?</h3>
            <p className="text-blue-800 text-sm">
              Salesforce doesn't trigger your flow once per record. Instead, it batches records together and triggers
              your flow once for the entire batch. A single flow execution might need to handle:
            </p>
            <ul className="list-disc list-inside text-blue-800 text-sm mt-2 ml-4">
              <li>200 records from a Data Loader import</li>
              <li>150 records updated by another automation</li>
              <li>100 records modified by an API integration</li>
            </ul>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div className="border-2 border-red-300 rounded p-4">
              <h3 className="font-semibold text-red-700 mb-2">❌ Non-Bulkified Flow</h3>
              <p className="text-sm text-gray-700">
                Processes one record at a time. Runs 200 times for 200 records. Easy to hit governor limits.
              </p>
            </div>
            <div className="border-2 border-green-300 rounded p-4">
              <h3 className="font-semibold text-green-700 mb-2">✅ Bulkified Flow</h3>
              <p className="text-sm text-gray-700">
                Processes all records together. Runs once for 200 records. Stays within governor limits.
              </p>
            </div>
          </div>
        </div>

        {/* How to Identify */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">How to Identify Non-Bulkified Flows</h2>

          <div className="space-y-4">
            <div>
              <h3 className="font-semibold text-gray-900 mb-2">In FlowDocs:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Check your Performance Scorecard on the flow detail page</li>
                <li>Look for "Not Bulkified" or "No Bulkification" in Issues Detected</li>
                <li>Grade D or F often indicates bulkification issues</li>
              </ul>
            </div>

            <div>
              <h3 className="font-semibold text-gray-900 mb-2">Signs Your Flow Isn't Bulkified:</h3>
              <ul className="list-disc list-inside text-gray-700 space-y-2 ml-4">
                <li>Your flow uses $Record variable instead of $Record__Prior or a collection</li>
                <li>You're not using Loop elements to process multiple records</li>
                <li>Your flow works fine in testing but fails in production with bulk operations</li>
                <li>You see "Too many SOQL queries" or "Too many DML statements" errors</li>
              </ul>
            </div>
          </div>
        </div>

        {/* The Two Approaches */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Two Approaches to Bulkification</h2>

          <div className="space-y-6">
            {/* Approach 1 */}
            <div className="border-l-4 border-blue-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">Approach 1: Collection-Based (Recommended)</h3>
              <p className="text-gray-700 mb-3">
                Process all trigger records together using collection variables and loops. Best for flows that
                need to query or update related data.
              </p>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-semibold text-gray-900 mb-2">When to use:</p>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside ml-2">
                  <li>Need to query related records</li>
                  <li>Need to create/update related records</li>
                  <li>Complex logic that varies by record</li>
                  <li>Need to collect data across all trigger records</li>
                </ul>
              </div>
            </div>

            {/* Approach 2 */}
            <div className="border-l-4 border-green-500 pl-4">
              <h3 className="font-semibold text-gray-900 mb-2">Approach 2: Fast Field Updates</h3>
              <p className="text-gray-700 mb-3">
                Use "Fast Field Updates" (Beta) for simple field updates without loops. Automatically
                processes all records in the batch.
              </p>
              <div className="bg-gray-50 p-3 rounded">
                <p className="text-sm font-semibold text-gray-900 mb-2">When to use:</p>
                <ul className="text-sm text-gray-700 space-y-1 list-disc list-inside ml-2">
                  <li>Simple field updates on the trigger record only</li>
                  <li>No need to query other records</li>
                  <li>No complex decision logic</li>
                  <li>Before Save flows</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        {/* Collection-Based Implementation */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Collection-Based Bulkification (Step-by-Step)</h2>

          <div className="bg-green-50 border-l-4 border-green-400 p-4 mb-6">
            <div className="flex">
              <LightBulbIcon className="h-6 w-6 text-green-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-green-800">The Core Pattern</h3>
                <p className="mt-2 text-sm text-green-700">
                  Store $Record in a collection → Loop through collection → Process each record →
                  Perform bulk operations after the loop
                </p>
              </div>
            </div>
          </div>

          <ol className="space-y-6">
            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  1
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Create a Collection Variable</h4>
                  <p className="text-gray-700 mt-1">
                    Create a collection variable to store all trigger records:
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2">
                    <p className="font-mono text-sm">Variable: triggerRecords</p>
                    <p className="font-mono text-sm">Type: Record Collection</p>
                    <p className="font-mono text-sm">Object: [Your Object, e.g., Account]</p>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  2
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Store $Record in Collection</h4>
                  <p className="text-gray-700 mt-1">
                    At the start of your flow, use Assignment to capture all trigger records:
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Assignment: triggerRecords = &#123;!$Record&#125;
                  </div>
                  <p className="text-gray-600 text-sm mt-2">
                    $Record represents ALL records that triggered the flow, not just one.
                  </p>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  3
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Loop Through the Collection</h4>
                  <p className="text-gray-700 mt-1">
                    Add a Loop element to iterate through each record:
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Loop: triggerRecords<br/>
                    Current Item: currentRecord
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  4
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Process Each Record</h4>
                  <p className="text-gray-700 mt-1">
                    Inside the loop, apply your business logic to currentRecord. Use Decisions,
                    Assignments, etc. Add records to other collections as needed.
                  </p>
                  <div className="bg-yellow-50 border border-yellow-200 p-3 rounded mt-2">
                    <p className="text-sm text-yellow-800">
                      <strong>Important:</strong> Don't query or do DML inside the loop!
                      Collect IDs/records, then query/update after the loop.
                    </p>
                  </div>
                </div>
              </div>
            </li>

            <li>
              <div className="flex items-start">
                <span className="flex-shrink-0 h-6 w-6 rounded-full bg-blue-100 text-blue-600 flex items-center justify-center text-sm font-medium mr-3">
                  5
                </span>
                <div className="flex-1">
                  <h4 className="font-medium text-gray-900">Bulk Operations After Loop</h4>
                  <p className="text-gray-700 mt-1">
                    After the loop completes, perform any queries or DML operations on entire collections:
                  </p>
                  <div className="bg-gray-100 p-3 rounded mt-2 font-mono text-sm">
                    Get Records WHERE Id IN &#123;!collectedIds&#125;<br/>
                    Update Records: &#123;!recordsToUpdate&#125;
                  </div>
                </div>
              </div>
            </li>
          </ol>
        </div>

        {/* Before/After Example */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Before & After Example</h2>

          <div className="grid md:grid-cols-2 gap-6">
            {/* Before */}
            <div className="border-2 border-red-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <XCircleIcon className="h-6 w-6 text-red-500 mr-2" />
                <h3 className="font-semibold text-red-700">Before (Non-Bulkified)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-red-600">// Processes ONE record only</div>
                <div>Decision: Check $Record.Type</div>
                <div className="pl-4 text-red-600">Get Records: Owner</div>
                <div className="pl-4 text-red-600">WHERE Id = $Record.OwnerId</div>
                <div className="pl-4 text-red-600">Update Records: $Record</div>
                <div className="pl-4 text-red-600">Set Owner Email</div>
              </div>
              <div className="mt-3 text-sm text-red-700">
                Problem: Only first record processed, others ignored
              </div>
            </div>

            {/* After */}
            <div className="border-2 border-green-300 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <CheckCircleIcon className="h-6 w-6 text-green-500 mr-2" />
                <h3 className="font-semibold text-green-700">After (Bulkified)</h3>
              </div>
              <div className="space-y-2 text-sm font-mono bg-gray-50 p-3 rounded">
                <div className="text-green-600">// Store trigger records</div>
                <div>Assignment: triggerRecords = $Record</div>
                <div className="text-green-600">// Loop processes ALL records</div>
                <div>Loop: triggerRecords</div>
                <div className="pl-4">Decision: Check currentRecord.Type</div>
                <div className="pl-8">Add OwnerId to ownerIds</div>
                <div className="text-green-600">// Bulk query after loop</div>
                <div>Get Records WHERE Id IN ownerIds</div>
                <div>Update Records: recordsToUpdate</div>
              </div>
              <div className="mt-3 text-sm text-green-700">
                Result: All 200 records processed correctly
              </div>
            </div>
          </div>
        </div>

        {/* Common Mistakes */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Common Bulkification Mistakes</h2>

          <div className="space-y-4">
            <div className="bg-red-50 border border-red-200 p-4 rounded">
              <h3 className="font-semibold text-red-800 mb-2">❌ Mistake #1: Using $Record Directly</h3>
              <p className="text-red-700 text-sm">
                Don't reference $Record.FieldName throughout your flow. Store it in a collection first,
                then loop through it.
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded">
              <h3 className="font-semibold text-red-800 mb-2">❌ Mistake #2: Queries/DML Inside Loops</h3>
              <p className="text-red-700 text-sm">
                Even if you use a loop, putting Get Records or Update Records inside the loop defeats
                the purpose. Collect data in the loop, operate after it.
              </p>
            </div>

            <div className="bg-red-50 border border-red-200 p-4 rounded">
              <h3 className="font-semibold text-red-800 mb-2">❌ Mistake #3: Not Testing with Bulk Data</h3>
              <p className="text-red-700 text-sm">
                Testing with one record won't reveal bulkification issues. Always test by updating
                50-100 records at once using Data Loader or a list view.
              </p>
            </div>
          </div>
        </div>

        {/* Testing Section */}
        <div className="bg-white shadow-sm rounded-lg p-6 mb-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Testing Your Bulkified Flow</h2>

          <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-4">
            <div className="flex">
              <BeakerIcon className="h-6 w-6 text-blue-400 mr-3 flex-shrink-0" />
              <div>
                <h3 className="text-sm font-medium text-blue-800">How to Test</h3>
                <ol className="mt-2 text-sm text-blue-700 space-y-1 list-decimal list-inside">
                  <li>Create a list view of 50-100 test records</li>
                  <li>Use "Mass Update" or Data Loader to update all records at once</li>
                  <li>Check that all records were processed correctly</li>
                  <li>Review debug logs for governor limit usage</li>
                </ol>
              </div>
            </div>
          </div>

          <div className="bg-green-50 p-4 rounded">
            <h3 className="font-semibold text-green-800 mb-2">What Success Looks Like:</h3>
            <ul className="text-green-700 text-sm space-y-1 list-disc list-inside ml-2">
              <li>All 100 records processed in a single flow execution</li>
              <li>SOQL queries: 1-5 (not 100+)</li>
              <li>DML statements: 1-5 (not 100+)</li>
              <li>No governor limit errors</li>
            </ul>
          </div>
        </div>

        {/* Impact */}
        <div className="bg-blue-50 border-l-4 border-blue-400 p-6 mb-6">
          <h2 className="text-xl font-bold text-blue-900 mb-2">Expected Improvement</h2>
          <div className="text-blue-800 space-y-2">
            <p className="font-semibold">Flow Executions: 99% reduction</p>
            <ul className="list-disc list-inside ml-4 space-y-1">
              <li>Before: 200 separate flow executions (one per record)</li>
              <li>After: 1 flow execution handling all 200 records</li>
            </ul>
            <p className="mt-4">
              Bulkification transforms a flow that breaks with bulk operations into one that can
              handle hundreds of records efficiently.
            </p>
          </div>
        </div>

        {/* Related Resources */}
        <div className="bg-white shadow-sm rounded-lg p-6">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Related Resources</h2>
          <ul className="space-y-3">
            <li>
              <Link to={`${basePath}/help/queries-in-loops`} className="text-blue-600 hover:text-blue-800 font-medium">
                Queries in Loops →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Avoiding queries inside loop structures</p>
            </li>
            <li>
              <Link to={`${basePath}/help/dml-in-loops`} className="text-blue-600 hover:text-blue-800 font-medium">
                DML Operations in Loops →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Avoiding DML inside loop structures</p>
            </li>
            <li>
              <Link to={`${basePath}/help/best-practices`} className="text-blue-600 hover:text-blue-800 font-medium">
                Flow Best Practices →
              </Link>
              <p className="text-gray-600 text-sm mt-1">Complete guide to high-performance flows</p>
            </li>
          </ul>
        </div>
      </div>

      <Chatbot />
    </div>
  );
}
